import { css } from '@emotion/react';
import { useRef, useState, useEffect } from 'react';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import NvTooltip from './nv-tooltip';


type TruncatedTextProps = {
  text: string,
  className: string,
  textWidth?: number,
  linesCount?: number,
  zIndex?: number,
  characterCount?: number,
  fixedHeight?: boolean,
};

/**
 * @param text text to truncate
 * @param className classname of the text
 * @param textWidth the width of the container that expects the overflow of text
 * @param linesCount pass only if the text need to display on multiple lines
 * @param characterCount maximum number of characters to display before truncating
 * @param fixedHeight when true with linesCount, uses fixed height instead of max-height (useful for aligned layouts)
 * @returns jsx element
 */

const NvTruncatedTextWithTooltip = ({
  text,
  className,
  textWidth,
  linesCount,
  zIndex,
  characterCount,
  fixedHeight = false,
}: TruncatedTextProps) => {
  const overflowingText = useRef(null);
  const [isOverflowActive, setOverflowActive] = useState(false);
  const [isSingleline, setIsSingleLine] = useState(false);

  const styles = css`
    ${linesCount && `
      -webkit-box-orient: vertical;
      display: -webkit-box;
      -webkit-line-clamp: ${linesCount};
      /* Height based on line-height (1.5em per line) + 1px to prevent premature truncation */
      ${fixedHeight ? `height: calc(${linesCount * 1.5}em + 1px);` : `max-height: calc(${linesCount * 1.5}em + 1px);`}
    `}
    ${isSingleline && linesCount && `
      display: flex !important;
      align-items: flex-end;
    `}
    width: ${textWidth ? `${textWidth}px` : '100%'};
    text-overflow: ellipsis;
    overflow: hidden;
    ${!linesCount && `
      white-space: nowrap;
    `}
    ${!isSingleline && !linesCount && `
      padding-bottom: ${quarterSpacing}px;
    `}
  `;

  const checkOverflow = (textContainer: HTMLSpanElement | null): boolean => {
    if (textContainer) {
      const { scrollHeight, clientHeight, scrollWidth, offsetWidth } = textContainer;
      return (scrollWidth > offsetWidth || scrollHeight > clientHeight);
    }
    return false;
  };

  useEffect(() => {
    setOverflowActive(checkOverflow(overflowingText.current));
  }, [isSingleline]);

  useEffect(() => {
    if (overflowingText.current && text) {
      const { scrollHeight, clientHeight } = overflowingText.current;
      setIsSingleLine(scrollHeight <= clientHeight);
    }
  }, [text]);

  const shouldTruncate = characterCount !== undefined && text.length > characterCount;
  const displayText = shouldTruncate ? `${text.slice(0, characterCount)}…` : text;

  return (
    <NvTooltip enabled={isOverflowActive || shouldTruncate} text={text} preventOverflow={false} zIndex={zIndex}>
      <div
        css={styles}
        className={className}
        ref={overflowingText}
      >
        {displayText}
      </div>
    </NvTooltip>
  );
};

export default NvTruncatedTextWithTooltip;
